<template>
  <Modal :loading="integration.busy" :title="title" noScroll @close="hide">
    <template #content>
      <div class="customer-select">
        <ul class="customer-modal__search">
          <li>
            <input
              type="text"
              class="search-field"
              placeholder="顧客名、顧客名（かな）、電話番号、会社名"
              v-model="_searchWord"
              @keydown.enter="handleSearchKeydown"
              @compositionstart="isComposing = true"
              @compositionend="isComposing = false"
            />
            <button class="search-btn" @click="getCustomerSelectList"></button>
          </li>
        </ul>
        <VirtualScroller
          class="customer-modal__customer-list"
          v-scroll-over="fetch"
          :items="customers"
          :heightCache="heights"
          min-item-height="96"
        >
          <template #before-container>
            <div v-show="isNoData" class="no-result">
              <CustomerIcon width="64" height="64" />
              <p>該当する顧客が見つかりませんでした。<br />検索キーワードをご確認ください。</p>
            </div>
          </template>
          <template v-if="isActive" v-slot="{ item, itemIndex, itemKey }">
            <CellMeasurer :key="itemKey" :heights="heights" :index="itemIndex">
              <div v-if="isVisibleIndex(itemIndex)" class="list index">
                <span>{{ item.index }}</span>
              </div>
              <div class="list">
                <a href="" @click.prevent="select(item)">
                  <div class="cstm-head">
                    <p class="cstm-name">{{ item.name }} <span>様</span></p>
                    <VisitCount :visitCount="item.visitCount" />
                  </div>
                  <p class="cstm-telnum">{{ item.supplementary }}</p>
                  <CustomerLabels class="labels" :bad="item.attributes.bad" :good="item.attributes.normal" />
                </a>
              </div>
            </CellMeasurer>
          </template>
          <template v-if="isGetCustomerSelectBusy" #after-container>
            <LoadingIndicator small />
          </template>
        </VirtualScroller>
      </div>
    </template>
  </Modal>
</template>

<script>
import { mapState } from 'vuex'
import LoadingIndicator from '@/components/molecules/LoadingIndicator'
import VirtualScroller, { CellMeasurer } from '@/components/molecules/VirtualScroller'
import CustomerLabels from '@/components/molecules/CustomerLabels'
import VisitCount from '@/components/molecules/VisitCount'
import Modal from '@/components/molecules/Modal'
import CustomerIcon from '@/components/icons/customer'
import ScrollOver from '@/directives/ScrollOver'

/**
 * 顧客一覧選択モーダル
 */
export default {
  name: 'CustomerSelectModal',
  components: {
    LoadingIndicator,
    VirtualScroller,
    CellMeasurer,
    CustomerLabels,
    VisitCount,
    CustomerIcon,
    Modal,
  },
  directives: {
    ScrollOver,
  },
  props: {
    title: String,
    ignoreIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      heights: [],
      isComposing: false,
    }
  },
  computed: {
    ...mapState({
      integration: state => state.customers.integration,
      searchWord: state => state.customer.customerSelectSearchWord,
      allCustomers: state => state.customer.customerSelectList,
      isGetCustomerSelectBusy: state => state.customer.isGetCustomerSelectBusy,
      isGetCustomerSelectEnd: state => state.customer.isGetCustomerSelectEnd,
    }),
    _searchWord: {
      get() {
        return this.searchWord
      },
      set(value) {
        this.$store.commit('customer/updateCustomerSelectSearchWord', value)
      },
    },
    customers() {
      return this.allCustomers.filter(customer => !this.ignoreIds.includes(customer.id))
    },
    isGetCustomerSelectListInfinityDisabled() {
      return this.isGetCustomerSelectBusy || this.isGetCustomerSelectEnd
    },
    isNoData() {
      return this.isGetCustomerSelectEnd && !this.customers.length
    },
    isActive() {
      return this.customers.length > 0
    },
  },
  watch: {
    allCustomers() {
      if (this.customers.length < 10) this.fetch()
    },
  },
  mounted() {
    this.$store.commit('customer/updateCustomerSelectSearchWord', '')
    this.getCustomerSelectList()
  },
  methods: {
    handleSearchKeydown() {
      if (!this.isComposing) {
        this.getCustomerSelectList()
      }
    },
    getCustomerSelectList() {
      const { commit } = this.$store
      commit('customer/updateIsGetCustomerSelectEnd', false)
      commit('customer/updateCustomerSelectList', [])
      commit('customer/updateCustomerSelectConditionStart', 0)
      this.fetch()
      this.heights = []
    },
    async fetch() {
      if (this.isGetCustomerSelectListInfinityDisabled) return
      await this.$store.dispatch('customer/getCustomerSelectList')
    },
    select(customer) {
      this.$emit('select', customer)
    },
    hide() {
      this.$emit('hide')
    },
    isVisibleIndex(index) {
      return index === 0 || this.customers[index].index !== this.customers[index - 1].index
    },
  },
}
</script>

<style lang="postcss" scoped>
.customer-select {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.customer-modal__search {
  background-color: var(--white);
  border-bottom: 1px solid var(--border-color);
  box-sizing: border-box;
  display: flex;
  height: 56px;
  justify-content: center;
  margin-top: 8px;

  li {
    &:first-child {
      display: flex;
    }
  }

  .search-field {
    appearance: none;
    border: 1px solid var(--dark-gray);
    border-radius: 4px 0 0 4px;
    box-sizing: border-box;
    color: var(--text-color);
    font-size: 13px;
    height: 40px;
    margin: 0;
    padding: 0 0 0 16px;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    width: 360px;

    &::-webkit-input-placeholder {
      color: var(--light-gray);
    }

    &:placeholder-shown {
      color: var(--dark-gray);
    }
  }

  .search-btn {
    background: url(/assets/img/icons/search.png) no-repeat 50%;
    background-size: 17px 17px;
    border: 1px solid var(--dark-gray);
    border-radius: 0 4px 4px 0;
    box-sizing: border-box;
    cursor: pointer;
    height: 40px;
    margin-left: -1px;
    padding: 0;
    text-indent: -9999px;
    width: 56px;
  }

  .responsive & {
    @media screen and (--sp-modal) {
      height: auto;
      margin: 0;
      padding: 16px;

      li {
        width: 100%;
      }

      .search-field {
        width: 100%;
      }
    }
  }
}

.customer-modal__customer-list {
  height: 520px;
  -webkit-overflow-scrolling: touch;
  overflow-x: hidden;
  overflow-y: scroll;
  position: relative;

  .index {
    background-color: var(--very-light-gray-2);
    box-sizing: border-box;
    height: 40px;
    margin-top: -1px;
    padding-left: 16px;

    span {
      color: var(--very-dark-gray-1);
      font-size: 21px;
      line-height: 1.9;
    }
  }

  .visit-count {
    display: grid;
    justify-self: flex-end;
  }

  a {
    box-sizing: border-box;
    display: block;
    min-height: 96px;
    padding: 16px 32px;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

    &.selected {
      background-color: var(--very-light-yellow);
    }
  }

  .labels {
    justify-content: flex-end;
  }

  .cstm-telnum {
    background: url(/assets/img/icons/confirm_tel.png) no-repeat;
    background-size: 16px 16px;
    color: var(--very-dark-gray-2);
    font-size: 13px;
    line-height: 1.6;
    margin-top: 8px;
    padding-left: 24px;
  }

  .no-result {
    align-items: center;
    display: flex;
    flex-direction: column;
    height: 520px;
    justify-content: center;

    svg {
      fill: var(--very-light-gray-3);
    }

    p {
      color: var(--dark-gray);
      text-align: center;
    }
  }

  .list {
    &::after {
      background-color: var(--border-color);
      content: '';
      display: block;
      height: 1px;
      margin: 0 auto;
      width: 416px;
    }

    &.index {
      span {
        color: var(--text-color);
      }

      &::after {
        display: none;
      }
    }

    .cstm-name {
      color: var(--text-color);
      font-weight: bold;
      line-height: 1;

      > span {
        font-size: 13px;
        font-weight: normal;
      }
    }
  }

  .items div:last-child .list::after {
    display: none;
  }

  .responsive & {
    @media screen and (--sp-modal) {
      flex: 1;
      height: auto;
    }
  }
}

.cstm-head {
  align-items: center;
  display: grid;
  grid-auto-flow: column;
}
</style>
